import type { ReactNode } from "react";
import { cn } from "@wags/ui/ui-utils";
import { hasPricingContent } from "../profile/section-content";
import type { Discount, Pricing, PricingFee } from "../profile/types";
import { formatMoneyRange, PRICE_DISPLAY_CLASS } from "@/lib/ui/price";
import { TINTED_PANEL_CLASS } from "@/components/shared/tintedPanel";

function feeRange(fee: PricingFee): string {
    const range =
        fee.typical != null
            ? { currency: fee.currency, min: fee.typical, max: fee.typical }
            : { currency: fee.currency, min: fee.min, max: fee.max };
    return formatMoneyRange(range) ?? "Contact for price";
}

function discountAside(discount: Discount): string | null {
    const amount =
        discount.amount != null
            ? formatMoneyRange({
                  currency: discount.currency,
                  min: discount.amount,
                  max: discount.amount,
              })
            : undefined;
    if (amount) return `-${amount}`;
    return discount.eligibility ?? null;
}

function ReceiptBox({ children }: { children: ReactNode }) {
    return (
        <div
            className={cn(
                TINTED_PANEL_CLASS,
                "divide-y divide-doberman-black/8",
            )}
        >
            {children}
        </div>
    );
}

const RECEIPT_ROW_CLASS =
    "flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0";

export function PricingSection({ pricing }: { pricing: Pricing }) {
    if (!hasPricingContent(pricing)) return null;

    const fee = pricing.fees[0];
    const showIncludes = fee.includes.length > 0;
    const showDiscounts = pricing.discounts.length > 0;

    return (
        <section className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-x-16">
            <div>
                <h2 className="text-section-title text-doberman-black text-balance">
                    What it costs
                </h2>
                <p className={cn(PRICE_DISPLAY_CLASS, "mt-3")}>
                    {feeRange(fee)}
                </p>
            </div>

            <div className="mt-5 flex flex-col gap-3 lg:mt-0 lg:max-w-190">
                {showIncludes && (
                    <div className="flex flex-col gap-2">
                        <p className="text-body-small text-doberman-black/55">
                            Included in the fee
                        </p>
                        <ReceiptBox>
                            {fee.includes.map((item) => (
                                <div key={item} className={RECEIPT_ROW_CLASS}>
                                    <span className="text-body text-doberman-black/85">
                                        {item}
                                    </span>
                                    <span className="shrink-0 text-body-small font-medium text-doberman-black/55">
                                        included
                                    </span>
                                </div>
                            ))}
                        </ReceiptBox>
                    </div>
                )}
                {showDiscounts && (
                    <ReceiptBox>
                        {pricing.discounts.map((discount) => {
                            const aside = discountAside(discount);
                            const subline =
                                discount.amount != null
                                    ? discount.eligibility
                                    : undefined;
                            return (
                                <div
                                    key={discount.name}
                                    className={RECEIPT_ROW_CLASS}
                                >
                                    <span className="flex flex-col">
                                        <span className="text-body text-doberman-black/85">
                                            {discount.name}
                                        </span>
                                        {subline && (
                                            <span className="text-body-small text-doberman-black/50">
                                                {subline}
                                            </span>
                                        )}
                                    </span>
                                    {aside && (
                                        <span className="shrink-0 text-body-small font-medium text-doberman-black/55">
                                            {aside}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </ReceiptBox>
                )}

                {pricing.notes && (
                    <p className="text-body-small text-doberman-black/50">
                        {pricing.notes}
                    </p>
                )}
            </div>
        </section>
    );
}
